import { useEffect, useState } from 'react';
import { Store } from 'redux';

import {
  enableClosedCaptioning,
  disableClosedCaptioning,
} from 'actions/closedCaptioning';
import { SET_CC_ALL } from 'actions/closedCaptionSettings';
import { ClosedCaptionStyleProps } from 'store-types';

export interface VegaCaptionPreferences {
  textSize?: 'very_small' | 'small' | 'normal' | 'large' | 'very_large';
  textColor?: string;
  textFont?: string;
  textEdgeStyle?: 'none' | 'uniform' | 'drop_shadowed' | 'raised' | 'depressed';
  textOpacity?: string;
  textBackgroundColor?: string;
  textBackgroundOpacity?: string;
  windowBackgroundColor?: string;
  windowBackgroundOpacity?: string;
}

export interface VegaCaptionSettings {
  enabled: boolean;
  preferences: VegaCaptionPreferences | null;
}

export interface VegaDevicePayload {
  appVersion?: string | null;
  deviceId: string | null;
  osVersion: string | null;
  osName: string | null;
  privacy: {
    adTrackingEnabled: boolean | null;
    limitAdTracking: boolean | null;
    advertisingId: string | null;
  };
  captions: VegaCaptionSettings;
}

declare global {
  interface Window {
    __VEGA_DEVICE__?: VegaDevicePayload;
  }
}

const SIZE_MAP: Record<string, string> = {
  very_small: 'xs',
  small: 'sm',
  normal: 'md',
  large: 'lg',
  very_large: 'xl',
};

const FONT_MAP: Record<string, string> = {
  monospace_serif: 'courier',
  monospace_sans: 'menlo',
  sans_serif: 'sansSerif',
  serif: 'iowan',
  casual: 'trebuchet',
  cursive: 'avenir',
  small_capitals: 'copperplate',
};

const EDGE_STYLE_MAP: Record<string, string> = {
  none: 'none',
  uniform: 'uniform',
  drop_shadowed: 'drop-shadow',
  raised: 'raised',
  depressed: 'depressed',
};

const OPACITY_MAP: Record<string, string> = {
  percent_100: 'solid',
  percent_75: 'translucent',
  percent_50: 'translucent',
  percent_25: 'transparent',
};

const mapColor = (color?: string): string | undefined =>
  color && color !== 'default' ? color : undefined;

export const mapVegaCaptionPreferences = (
  preferences: VegaCaptionPreferences,
): Partial<ClosedCaptionStyleProps> => {
  const mapped: Record<string, string | undefined> = {
    textSize: preferences.textSize && SIZE_MAP[preferences.textSize],
    textColor: mapColor(preferences.textColor),
    textType: preferences.textFont && FONT_MAP[preferences.textFont],
    textOpacity: preferences.textOpacity && OPACITY_MAP[preferences.textOpacity],
    textShadow:
      preferences.textEdgeStyle && EDGE_STYLE_MAP[preferences.textEdgeStyle],
    backgroundColor: mapColor(preferences.textBackgroundColor),
    backgroundOpacity:
      preferences.textBackgroundOpacity &&
      OPACITY_MAP[preferences.textBackgroundOpacity],
    windowColor: mapColor(preferences.windowBackgroundColor),
    windowOpacity:
      preferences.windowBackgroundOpacity &&
      OPACITY_MAP[preferences.windowBackgroundOpacity],
  };

  return Object.keys(mapped).reduce((acc, key) => {
    if (mapped[key]) acc[key] = mapped[key];
    return acc;
  }, {} as Record<string, string | undefined>) as Partial<ClosedCaptionStyleProps>;
};

export const getVegaDeviceInfo = (): VegaDevicePayload | undefined =>
  window.__VEGA_DEVICE__;

const VEGA_DEVICE_UPDATED_EVENT = 'vega:device-updated';

const notifyVegaDeviceUpdated = (): void => {
  window.dispatchEvent(new Event(VEGA_DEVICE_UPDATED_EVENT));
};

export const useVegaDeviceInfo = (): VegaDevicePayload | undefined => {
  const [device, setDevice] = useState(() => getVegaDeviceInfo());

  useEffect(() => {
    const onUpdate = () => setDevice(getVegaDeviceInfo());
    window.addEventListener(VEGA_DEVICE_UPDATED_EVENT, onUpdate);
    return () => window.removeEventListener(VEGA_DEVICE_UPDATED_EVENT, onUpdate);
  }, []);

  return device;
};

const applyCaptionSettings = (
  store: Store,
  captions: VegaCaptionSettings,
): void => {
  // Reflect the device-level captions on/off state in the player.
  store.dispatch(
    captions.enabled ? enableClosedCaptioning() : disableClosedCaptioning(),
  );

  if (captions.preferences) {
    const styles = mapVegaCaptionPreferences(captions.preferences);
    if (Object.keys(styles).length) {
      store.dispatch({ type: SET_CC_ALL, payload: styles });
    }
  }
};

export const initVegaDeviceBridge = (store: Store): (() => void) => {
  if (!window.ReactNativeWebView) return () => {};

  let deviceCcEnabled: boolean | null = null;

  const applyFromDevice = (captions: VegaCaptionSettings) => {
    deviceCcEnabled = !!captions.enabled;
    applyCaptionSettings(store, captions);
  };

  const onMessage = (e: MessageEvent) => {
    try {
      const msg = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
      if (msg?.type === 'vega:device' && msg.data) {
        window.__VEGA_DEVICE__ = msg.data as VegaDevicePayload;
        notifyVegaDeviceUpdated();
        if (msg.data.captions) {
          applyFromDevice(msg.data.captions as VegaCaptionSettings);
        }
      } else if (msg?.type === 'vega:captions' && msg.data) {
        if (window.__VEGA_DEVICE__) {
          window.__VEGA_DEVICE__.captions = msg.data as VegaCaptionSettings;
          notifyVegaDeviceUpdated();
        }
        applyFromDevice(msg.data as VegaCaptionSettings);
      }
    } catch {
      // Not a JSON bridge message — ignore
    }
  };
  window.addEventListener('message', onMessage);

  const unsubscribe = store.subscribe(() => {
    const enabled = (store.getState() as { closedCaptioning?: boolean })
      .closedCaptioning;
    if (
      typeof enabled === 'boolean' &&
      deviceCcEnabled !== null &&
      enabled !== deviceCcEnabled
    ) {
      deviceCcEnabled = enabled;
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({ type: 'captions:set', data: { enabled } }),
      );
    }
  });

  window.ReactNativeWebView.postMessage(
    JSON.stringify({ type: 'device:request' }),
  );

  return () => {
    window.removeEventListener('message', onMessage);
    unsubscribe();
  };
};
